'use client';
import React from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { Divider } from '@/components/atoms/divider';
import Heading from '@/components/atoms/heading';
import { Text } from '@/components/atoms/text';
import { G2Badge } from '@/components/integrations/g2';
import { ProxywayBadge } from '@/components/integrations/proxyway';
import { TrustpilotBadge } from '@/components/integrations/trustpilot';
import { VideoPopup, VideoPopupProps } from '@/components/molecules/videoPopup';
//will be needed for the when the component will be moved to decodo
// import {
//   VideoPopup,
//   VideoPopupProps,
// } from '@/decodo/components/atoms/videoPopup';

import IconShield from '~/svg/IconShield.svg';

export type HeroProps = {
  title: string;
  description: React.ReactNode;
  image: React.ReactNode;
  links: React.ReactNode;
  features?: React.ReactNode;
  videoProps?: VideoPopupProps['videoProps'];
  showMoneyBack?: boolean;
  areReviewsVisible?: boolean;
};

export function Hero({
  image,
  links,
  title,
  description,
  features,
  videoProps,
  showMoneyBack,
  areReviewsVisible = true,
}: HeroProps) {
  const { texts } = useTranslation();

  return (
    <div className='flex w-full flex-row items-center gap-6'>
      <div className='flex w-full flex-col'>
        {areReviewsVisible && (
          <div className='mb-6 flex gap-3'>
            <TrustpilotBadge />
            <div>
              <Divider
                showOnDark
                alignment='vertical'
                classNames={{ root: 'border-neutral-dark-N50' }}
              />
            </div>
            <G2Badge />
            <div>
              <Divider
                showOnDark
                alignment='vertical'
                classNames={{ root: 'border-neutral-dark-N50' }}
              />
            </div>
            <ProxywayBadge />
          </div>
        )}
        <Heading level='h1' classNames={{ root: 'mb-6' }}>
          {title}
        </Heading>
        <div className='text-neutral-N99 mb-9'>{description}</div>
        {links && (
          <div
            className={cn(
              'flex flex-wrap gap-6',
              showMoneyBack ? 'mb-4' : 'mb-9'
            )}
            data-tracker-component='buttons'
          >
            {links}
          </div>
        )}
        {showMoneyBack && (
          <div
            className={cn('flex items-center gap-2', features ? 'mb-9' : '')}
          >
            <IconShield className='text-success-S99 h-4 w-4' />
            <Text variant='body-s' color='neutral-N50'>
              {texts.pricing_cards.money_back.money_back_option}
            </Text>
          </div>
        )}
        {features && (
          <div className='flex flex-wrap items-center gap-x-9 gap-y-6'>
            {features}
          </div>
        )}
      </div>
      <div className='relative hidden h-full w-full max-w-[360px] items-center justify-center md:flex'>
        {image}
        {videoProps && (
          <VideoPopup
            classNames={{
              targetWrapper:
                'absolute left-[50%] top-[50%] -translate-x-2/4 -translate-y-2/4',
            }}
            videoProps={videoProps}
          />
        )}
      </div>
    </div>
  );
}
